/**
 * String Formatting Utilities
 *
 * Centralized string manipulation functions for consistent formatting across the application.
 * These utilities handle common transformations like slug-to-title conversion, underscore
 * replacement, and title-casing.
 */

/**
 * Converts a slug or snake_case string to Title Case with spaces.
 * Handles both underscores and hyphens as word separators.
 *
 * @param {string} text - The slug or snake_case string to format
 * @returns {string} - Title-cased string with spaces (e.g., "my_example_slug" → "My Example Slug")
 *
 * @example
 * formatSlugToTitle("final_wishes") // → "Final Wishes"
 * formatSlugToTitle("health-poa") // → "Health Poa"
 * formatSlugToTitle("account_agent") // → "Account Agent"
 */
export function formatSlugToTitle(text) {
  if (!text || typeof text !== 'string') return '';
  
  return text
    .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
}

/**
 * Converts a Title Case string back to snake_case slug format.
 * Useful for reverse-mapping display labels to API slugs.
 *
 * @param {string} text - The Title Case string to convert
 * @returns {string} - snake_case slug (e.g., "Final Wishes" → "final_wishes")
 *
 * @example
 * formatTitleToSlug("Final Wishes") // → "final_wishes"
 * formatTitleToSlug("Health POA") // → "health_poa"
 */
export function formatTitleToSlug(text) {
  if (!text || typeof text !== 'string') return '';
  
  return text
    .toLowerCase()
    .replace(/\s+/g, '_'); // Replace spaces with underscores
}

/**
 * Converts a Title Case string to kebab-case slug format.
 * Useful for URL-friendly slugs.
 *
 * @param {string} text - The Title Case string to convert
 * @returns {string} - kebab-case slug (e.g., "Final Wishes" → "final-wishes")
 *
 * @example
 * formatTitleToKebab("Final Wishes") // → "final-wishes"
 * formatTitleToKebab("Health POA") // → "health-poa"
 */
export function formatTitleToKebab(text) {
  if (!text || typeof text !== 'string') return '';
  
  return text
    .toLowerCase()
    .replace(/\s+/g, '-'); // Replace spaces with hyphens
}

/**
 * Replaces underscores with spaces without changing case.
 * Useful for simple display transformations where title-casing is not desired.
 *
 * @param {string} text - The string with underscores
 * @returns {string} - String with spaces instead of underscores
 *
 * @example
 * replaceUnderscores("my_example_text") // → "my example text"
 */
export function replaceUnderscores(text) {
  if (!text || typeof text !== 'string') return '';
  
  return text.replace(/_/g, ' ');
}

/**
 * Formats a hierarchySection or templateSlug value for display in sort dropdowns.
 * This is the canonical formatter for Digital Vault and similar UI components.
 *
 * @param {string} raw - Raw section or slug string from the API
 * @returns {string} - Formatted display label
 *
 * @example
 * formatSortLabel("final_wishes") // → "Final Wishes"
 * formatSortLabel("my-section") // → "My Section"
 */
export function formatSortLabel(raw) {
  return formatSlugToTitle(raw);
}

/**
 * Formats a release timing value for display.
 * Converts snake_case timing codes to readable labels.
 *
 * @param {string} timing - Release timing code (e.g., "incap_immediate")
 * @returns {string} - Formatted timing label (e.g., "Incap Immediate")
 *
 * @example
 * formatReleaseTiming("incap_immediate") // → "Incap Immediate"
 * formatReleaseTiming("death_after_cooling") // → "Death After Cooling"
 */
export function formatReleaseTiming(timing) {
  return formatSlugToTitle(timing);
}

/**
 * Formats a display title or body from role requests.
 * Replaces underscores with spaces for better readability.
 *
 * @param {string} text - Display title or body text
 * @returns {string} - Formatted text with spaces
 *
 * @example
 * formatDisplayText("role_request_pending") // → "role request pending"
 */
export function formatDisplayText(text) {
  return replaceUnderscores(text);
}
